/**
 * Export Buttons Component
 *
 * Provides buttons to export table data to Excel (.xlsx) or to print it.
 */

import { Download, FileSpreadsheet, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from '@/hooks/useTranslation'
import { buildXlsx, XLSX_MIME } from '@/lib/xlsx'

interface ExportButtonsProps<T extends object = Record<string, unknown>> {
  data: T[]
  filename: string
  columns: {
    key: keyof T | string
    header: string
    format?: (value: unknown) => string
    /**
     * Marks a column as numeric-but-not-a-quantity (consecutivos, CABYS,
     * cédulas, códigos). Currently inert: the .xlsx writes every cell as a
     * string, so those values already keep their leading zeros and never turn
     * into scientific notation. It only mattered for the CSV export, which is
     * disabled — kept so the intent survives if CSV comes back.
     */
    asText?: boolean
    /**
     * Append a sum of this column as a totals row at the end of the export.
     * The summed value is rendered through the column's `format` so it matches
     * the cell formatting (currency/number).
     */
    total?: boolean
  }[]
}

/**
 * Build the formatted cells for the totals row, or null if no column opts in.
 *
 * Columns flagged with `total` are summed across the data and run through their
 * `format`; the `totalsLabel` is placed in the first non-total column so the row
 * reads e.g. "Totales | | | 1,234.00 | 160.42 | 1,394.42".
 */
function buildTotalsCells<T extends object>(
  data: T[],
  columns: ExportButtonsProps<T>['columns'],
  totalsLabel: string
): string[] | null {
  if (!columns.some((col) => col.total)) return null
  let labelPlaced = false
  return columns.map((col) => {
    if (col.total) {
      const sum = data.reduce((acc, row) => {
        const value = (row as Record<string, unknown>)[col.key as string]
        const n = typeof value === 'number' ? value : Number(value)
        return acc + (Number.isFinite(n) ? n : 0)
      }, 0)
      return col.format ? col.format(sum) : String(sum)
    }
    if (!labelPlaced) {
      labelPlaced = true
      return totalsLabel
    }
    return ''
  })
}

/** Hand a blob to the browser as a download. */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Escape a value for safe inclusion in HTML markup (used by the print view).
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Build the formatted cell strings for one data row.
 */
function formatRow<T extends object>(
  row: T,
  columns: ExportButtonsProps<T>['columns']
): string[] {
  return columns.map((col) => {
    const value = (row as Record<string, unknown>)[col.key as string]
    return col.format ? col.format(value) : String(value ?? '')
  })
}

/**
 * Export to Excel as a real .xlsx workbook.
 *
 * This used to emit an HTML table named `.xls`, which made Excel warn that the
 * format and the extension don't match — and refuse to open it outright where
 * the Office policy blocks that mismatch.
 */
function exportExcel<T extends object>(
  data: T[],
  columns: ExportButtonsProps<T>['columns'],
  filename: string,
  totalsLabel: string
) {
  const rows = data.map((row) => formatRow(row, columns))
  const totalsCells = buildTotalsCells(data, columns, totalsLabel)
  if (totalsCells) rows.push(totalsCells)

  const workbook = buildXlsx({
    name: filename,
    headers: columns.map((col) => col.header),
    rows,
    boldLastRow: totalsCells !== null,
  })

  downloadBlob(new Blob([workbook], { type: XLSX_MIME }), `${filename}.xlsx`)
}

/**
 * Print data as a table
 */
function printTable<T extends object>(
  data: T[],
  columns: ExportButtonsProps<T>['columns'],
  title: string,
  totalsLabel: string
) {
  const printWindow = window.open('', '_blank')
  if (!printWindow) return

  const totalsCells = buildTotalsCells(data, columns, totalsLabel)
  const footRow = totalsCells
    ? `<tfoot><tr>${totalsCells
        .map((cell) => `<td style="font-weight:bold;">${escapeHtml(cell)}</td>`)
        .join('')}</tr></tfoot>`
    : ''

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${escapeHtml(title)}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        h1 { font-size: 18px; margin-bottom: 20px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; font-weight: bold; }
        tr:nth-child(even) { background-color: #f9f9f9; }
        @media print {
          body { margin: 0; }
        }
      </style>
    </head>
    <body>
      <h1>${escapeHtml(title)}</h1>
      <table>
        <thead>
          <tr>
            ${columns.map((col) => `<th>${escapeHtml(col.header)}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${data
            .map(
              (row) => `
            <tr>
              ${formatRow(row, columns)
                .map((formatted) => `<td>${escapeHtml(formatted)}</td>`)
                .join('')}
            </tr>
          `
            )
            .join('')}
        </tbody>
        ${footRow}
      </table>
      <script>
        window.onload = function() { window.print(); }
      </script>
    </body>
    </html>
  `

  printWindow.document.write(html)
  printWindow.document.close()
}

/**
 * Export Buttons Component
 */
export function ExportButtons<T extends object>({ data, filename, columns }: ExportButtonsProps<T>) {
  const { t } = useTranslation()

  if (!data || data.length === 0) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          {t('export.export')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* No hay CSV a proposito (2026-09-16). En CSV los codigos largos tienen
            que ir como formula ="..." para que Excel no los pase a notacion
            cientifica, y eso se ve literal en LibreOffice: no existe una forma
            que sirva en los dos. Con el .xlsx real el CSV dejo de hacer falta
            para leer. Si alguien necesita alimentar otro sistema, el generador
            esta en el historial de git (commit que quito el CSV). */}
        <DropdownMenuItem onClick={() => exportExcel(data, columns, filename, t('export.totals'))}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          {t('export.excel')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => printTable(data, columns, filename, t('export.totals'))}>
          <Printer className="h-4 w-4 mr-2" />
          {t('export.print')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
